import BannerType from '../../types/banner/BannerType';
import BannerStyle from '../../types/banner/BannerStyle';
import AdPages from '../../types/AdPages';
import AdZones from '../../types/AdZones';

export const MAIN_FOOTER_BANNER_ASPECT_RATIO = 2.85;
export const MAIN_FOOTER_MOBILE_SQUARE_ASPECT_RATIO = 1;

/**
 * Map admin carousel style to slick flags + in-slide layout for main/footer.
 *
 * - INFINITE: loop; creative left in a full-width slide (empty right when narrow).
 * - CENTERED: no slick peeks — creative centered in a full-width slide; empty
 *   sides OK when narrow; infinite false.
 * - BOTH: infinite + centerMode peeks so neighbours fill sides when the creative
 *   is narrower than the list; creative centered (incl. overflow crop).
 *
 * `useCreativeSlideWidth` is true for BOTH (caller still requires >1 slide).
 *
 * @param {string} style BannerStyle value
 * @returns {{
 *   infinite: boolean,
 *   centerMode: boolean,
 *   contentJustify: 'flex-start'|'center',
 *   useCreativeSlideWidth: boolean,
 * }}
 */
export const getBannerCarouselLayout = (style) => {
  switch (style) {
    case BannerStyle.INFINITE:
      return {
        infinite: true,
        centerMode: false,
        contentJustify: 'flex-start',
        useCreativeSlideWidth: false,
      };
    case BannerStyle.CENTERED:
      return {
        infinite: false,
        centerMode: false,
        contentJustify: 'center',
        useCreativeSlideWidth: false,
      };
    case BannerStyle.BOTH:
      return {
        infinite: true,
        centerMode: true,
        contentJustify: 'center',
        useCreativeSlideWidth: true,
      };
    default:
      return {
        infinite: false,
        centerMode: false,
        contentJustify: 'flex-start',
        useCreativeSlideWidth: false,
      };
  }
};

/**
 * Slick-only flags from {@link getBannerCarouselLayout} (omit false flags so
 * BannerSlider defaults stay in control when spreading overrides).
 *
 * @param {string} style BannerStyle value
 * @returns {{ infinite?: boolean, centerMode?: boolean }}
 */
export const getBannerCarouselSettings = (style) => {
  const { infinite, centerMode } = getBannerCarouselLayout(style);
  const settings = {};
  if (infinite) {
    settings.infinite = true;
  }
  if (centerMode) {
    settings.centerMode = true;
  }
  return settings;
};

/**
 * @param {string} style BannerStyle value
 * @returns {'flex-start'|'center'}
 */
export const getBannerSlideContentJustify = (style) =>
  getBannerCarouselLayout(style).contentJustify;

/**
 * Zone meta for main/footer slot decisions (e.g. has_mobile_image → 1:1).
 * Main passes bannerPage. Footer has no page prop — prefer FRONT_PAGE, then
 * any page that exposes the zone.
 *
 * @param {{ getZoneMeta?: Function, bannerZones?: object }} adStore
 * @param {string} adZone
 * @param {string} [bannerPage]
 * @returns {{ has_mobile_image?: boolean } | null}
 */
export const resolveBannerZoneMeta = (adStore, adZone, bannerPage) => {
  if (!adStore || typeof adStore.getZoneMeta !== 'function') {
    return null;
  }

  if (bannerPage) {
    return adStore.getZoneMeta(bannerPage, adZone) || null;
  }

  const pageKeys = adStore.bannerZones ? Object.keys(adStore.bannerZones) : [];
  const ordered = [
    AdPages.FRONT_PAGE,
    ...pageKeys.filter((key) => key !== AdPages.FRONT_PAGE),
  ];

  for (let i = 0; i < ordered.length; i++) {
    const meta = adStore.getZoneMeta(ordered[i], adZone);
    if (meta) {
      return meta;
    }
  }

  return null;
};

/**
 * Shared Main/Footer slot props. Same carousel + mobile 1:1 rules; only
 * configured desktop height differs per zone.
 *
 * @param {{
 *   adZone: string,
 *   configStore: { banner: { mainBanner: object, footerBanner: object } },
 *   uiStore: { isSmallest: boolean, isMobile: boolean },
 *   adStore: object,
 *   bannerPage?: string,
 *   viewportWidth?: number,
 * }} args
 * @returns {{
 *   aspectRatio: number,
 *   maximumCrossAxisSize: number,
 *   infinite: boolean,
 *   centerMode: boolean,
 *   contentJustify: 'flex-start'|'center',
 *   useCreativeSlideWidth: boolean,
 * }}
 */
export const resolveMainFooterBannerSlot = ({
  adZone,
  configStore,
  uiStore,
  adStore,
  bannerPage,
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0,
}) => {
  const isMain = adZone === AdZones.MAIN_BANNER;
  const bannerConfig = isMain
    ? configStore.banner.mainBanner
    : configStore.banner.footerBanner;
  const zoneMeta = resolveBannerZoneMeta(adStore, adZone, bannerPage);
  const useMobileSquare = uiStore.isSmallest && !!zoneMeta?.has_mobile_image;

  const aspectRatio = useMobileSquare
    ? MAIN_FOOTER_MOBILE_SQUARE_ASPECT_RATIO
    : MAIN_FOOTER_BANNER_ASPECT_RATIO;

  const maximumCrossAxisSize = useMobileSquare
    ? viewportWidth
    : uiStore.isMobile
    ? viewportWidth / MAIN_FOOTER_BANNER_ASPECT_RATIO
    : bannerConfig.height;

  return {
    aspectRatio,
    maximumCrossAxisSize,
    ...getBannerCarouselLayout(bannerConfig.style),
  };
};

/**
 * Whether non-infinite banner autoplay should schedule a jump back to slide 0.
 * Only after the last slide — scheduling on every afterChange races slick
 * autoplay and produces 1-2-1-2-3-… resets (CENTERED mode).
 *
 * @param {number} slideCount
 * @param {number|null|undefined} currentSlide
 * @returns {boolean}
 */
export const shouldLoopNonInfiniteBannerToStart = (
  slideCount,
  currentSlide
) => {
  if (
    !slideCount ||
    currentSlide === undefined ||
    currentSlide === null ||
    currentSlide < 0
  ) {
    return false;
  }

  return currentSlide >= slideCount - 1;
};

/**
 * Resolve horizontal slide frame width for configured-slot banners.
 *
 * - Full-frame (`useCreativeSlideWidth` false): track-safe / list width so empty
 *   side space lives inside the slide (INFINITE / CENTERED).
 * - Peek (`useCreativeSlideWidth` true): size to the creative so neighbours can
 *   show beside a *narrow* image, but cap at the full list width — never at the
 *   centerPadding-reduced max (that crops full-bleed creatives to force peeks).
 *
 * @param {{ creativeWidth: number, listWidth?: number, trackSafeMax?: number, useCreativeSlideWidth?: boolean }} args
 * @returns {number}
 */
export const resolveBannerSlideMainAxisSize = ({
  creativeWidth,
  listWidth,
  trackSafeMax,
  useCreativeSlideWidth = false,
}) => {
  const listCap = listWidth ? Math.floor(listWidth) : 0;
  const trackCap = trackSafeMax ? Math.floor(trackSafeMax) : 0;

  if (!useCreativeSlideWidth) {
    return trackCap || listCap || creativeWidth;
  }

  const peekCap = listCap || trackCap || creativeWidth;
  return Math.min(creativeWidth, peekCap);
};

/**
 * Overlay matches the visible creative box: image width when fully shown,
 * slide width when the creative is cropped by the frame.
 *
 * @param {number} creativeWidth
 * @param {number} slideWidth
 * @returns {number}
 */
export const resolveBannerOverlayMainAxisSize = (creativeWidth, slideWidth) =>
  Math.min(creativeWidth, slideWidth);

/**
 * centerPadding after override merge. Non-centered carousels use 0 so slides
 * reach the list edge. ONLY slick centerMode (BOTH) uses peek gutters so
 * neighbours are visible — slide width must be capped with getMaximumSlideWidth.
 *
 * @param {boolean} centerMode
 * @param {object} [overrideSettings]
 * @param {string} [defaultPeekPadding='50px']
 * @returns {string|number}
 */
export const resolveBannerCenterPadding = (
  centerMode,
  overrideSettings,
  defaultPeekPadding = '50px'
) => {
  if (overrideSettings && 'centerPadding' in overrideSettings) {
    return overrideSettings.centerPadding;
  }

  return centerMode ? defaultPeekPadding : '0px';
};

/**
 * Size an image to fill the slider slot height. Width follows the image aspect
 * ratio and may exceed the viewport; the slide clips overflow at the sides.
 * Images must not be scaled down to fit width — that would letterbox the slot.
 * FILE videos may letterbox; images always fill the slot vertically.
 *
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {number} slotHeight
 * @returns {{ width: number, height: number } | null}
 */
export const countImageSizeForSlotHeight = (
  imageWidth,
  imageHeight,
  slotHeight
) => {
  if (!imageWidth || !imageHeight || !slotHeight) {
    return null;
  }

  const height = Math.round(slotHeight);

  return {
    width: Math.round((height * imageWidth) / imageHeight),
    height,
  };
};

/**
 * Lazy-load placeholder height. Prefer the slot fallback (aspect-ratio + max
 * height + scent). Clear after the slider has been measured to avoid locking CLS.
 *
 * @param {boolean} hasMeasuredSlider
 * @param {number} fallbackHeight
 * @returns {number | undefined}
 */
export const getLazyLoadHeight = (hasMeasuredSlider, fallbackHeight) => {
  if (hasMeasuredSlider) {
    return undefined;
  }

  return fallbackHeight;
};

/**
 * Visibility filter for banner slider slides.
 * On the smallest breakpoint, if any slide has a mobile image, only keep
 * creatives that can render in mobile format (mobile image, video, or
 * text-only). Desktop-only image banners are omitted to avoid blank slides
 * in the 1:1 mobile carousel. Videos are always kept (no separate mobile
 * video asset).
 *
 * @param {Array} ads
 * @param {boolean} isSmallest
 * @returns {Array}
 */
export const filterVisibleBannerSlides = (ads, isSmallest) => {
  const visible = [...(ads || [])].filter((banner) => {
    return (
      (isSmallest && !banner.hide_from_mobile_users) ||
      (!isSmallest && !banner.hide_from_desktop_tablet_users)
    );
  });

  if (!isSmallest) {
    return visible;
  }

  const hasMobileImageSlide = visible.some((banner) => !!banner.mobile_image);
  if (!hasMobileImageSlide) {
    return visible;
  }

  return visible.filter((banner) => {
    if (banner.type === BannerType.VIDEO) {
      return true;
    }

    // Text-only banners have neither desktop nor mobile image.
    if (!banner.image && !banner.mobile_image) {
      return true;
    }

    return !!banner.mobile_image;
  });
};

/**
 * Active image dimensions for the current viewport mode.
 *
 * @param {object} banner
 * @param {boolean} useMobileImage
 * @returns {{ width: number|null|undefined, height: number|null|undefined }}
 */
export const getBannerImageDimensions = (banner, useMobileImage) => {
  if (useMobileImage && banner.mobile_image) {
    return {
      width: banner.mobile_image_width,
      height: banner.mobile_image_height,
    };
  }

  return {
    width: banner.image_width,
    height: banner.image_height,
  };
};

/**
 * Intrinsic size from a loaded &lt;img&gt; when the API left width/height null
 * (common for SVG). Prefer naturalWidth/Height; fall back to rendered box.
 *
 * @param {HTMLImageElement|null|undefined} img
 * @returns {{ width: number, height: number } | null}
 */
export const measureBannerImageIntrinsicSize = (img) => {
  if (!img) {
    return null;
  }

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  if (naturalWidth > 0 && naturalHeight > 0) {
    return {
      width: naturalWidth,
      height: naturalHeight,
    };
  }

  const renderedWidth =
    img.clientWidth || Math.round(img.getBoundingClientRect().width);
  const renderedHeight =
    img.clientHeight || Math.round(img.getBoundingClientRect().height);
  if (renderedWidth > 0 && renderedHeight > 0) {
    return {
      width: renderedWidth,
      height: renderedHeight,
    };
  }

  return null;
};

/**
 * API dims when present; otherwise measured intrinsic dims for the banner id.
 *
 * @param {object} banner
 * @param {boolean} useMobileImage
 * @param {Object.<string, { width: number, height: number }>} [measuredImageSizes]
 * @returns {{ width: number|null|undefined, height: number|null|undefined }}
 */
export const resolveBannerImageDimensions = (
  banner,
  useMobileImage,
  measuredImageSizes
) => {
  const apiDims = getBannerImageDimensions(banner, useMobileImage);
  if (apiDims.width && apiDims.height) {
    return apiDims;
  }

  const measured = measuredImageSizes && measuredImageSizes[banner.id];
  if (measured?.width && measured?.height) {
    return measured;
  }

  return apiDims;
};

/**
 * Total cross-axis size of a configured main/footer slot. Includes the scent
 * strip below the media; pass scentHeight 0 when there is no scent or the
 * scent is drawn on top of the media. Image and letterboxed FILE-video slides
 * must use the same value so scents line up across mixed carousels.
 *
 * @param {number} crossAxisSize
 * @param {number} scentHeight
 * @returns {number}
 */
export const getConfiguredSlotCrossAxisSize = (
  crossAxisSize,
  scentHeight = 0
) => crossAxisSize + (scentHeight || 0);
